import { Injectable, InjectionToken, inject } from '@angular/core';
import { SettingsState } from '@library/utils/interfaces/settings-state.interface';
import { Actions, ofType } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
import { ConnectableObservable, Observable } from 'rxjs';
import { map, publishReplay, take, tap } from 'rxjs/operators';
import { RHCConfigurationPayload } from '../modules/modules.interface';
import {
  GetHomeConfigFailure,
  GetHomeConfigSuccess,
  GetHomeNFAFailure,
  GetHomeNFASuccess,
  GetHomeStatusFailure,
  GetHomeStatusSuccess,
  HomesActions,
  HomesActionsUnion,
  LoadComparisonDataSuccess,
  LoadForecastSuccess,
  SetHomeSizeSuccess,
} from './homes.action';
import {
  AckLocalResourcesPayload,
  AutoTempModes,
  CoolingModes,
  GetHomeInfoPayload,
  LoadComparisonDataPayload,
  LoadForecastPayload,
  PlaceUpdate,
  SetHeatingTypeForHeatingControllerPayload,
  SetHeatingTypeForHousePayload,
  SetHomeConfigPayload,
  SetHomeSizePayload,
  SetHomeTypePayload,
  SetStatePayload,
  SetThermPointMode,
  TemperatureControlModes,
  ThermModes,
} from './homes.interface';
import * as HomesSelectors from './homes.selector';

export const HOMES_FACADE = new InjectionToken('HOMES_FACADE');

/**
 * Has to be extended by a specific facade for each project
 * Extending facade must have {providedIn: 'root'} in its Injectable decorator
 */
@Injectable()
export class HomesFacade {
  protected store$ = inject<Store<SettingsState>>(Store);
  protected actions$ = inject(Actions);

  homes$ = this.store$.pipe(select(HomesSelectors.getHomes));
  currentHome$ = this.store$.pipe(select(HomesSelectors.getCurrentHome));
  currentHomeGateway$ = this.store$.pipe(select(HomesSelectors.getCurrentHomeGateway));
  thermoregulationConfig$ = this.store$.pipe(select(HomesSelectors.getThermoregulationConfig));
  currentHomeId$ = this.store$.pipe(select(HomesSelectors.getCurrentHomeId));
  currentHomePersons$ = this.store$.pipe(select(HomesSelectors.getCurrentHomePersons));
  isLoadingHomes$ = this.store$.pipe(select(HomesSelectors.isLoadingHomes));
  errorModalDisplay$ = this.store$.pipe(select(HomesSelectors.getErrorModalDisplay));
  homeUsers$ = this.store$.pipe(select(HomesSelectors.getHomeUsers));
  adminAccessCode$ = this.store$.pipe(select(HomesSelectors.getAdminAccessCode));
  homeTimezone$ = this.store$.pipe(select(HomesSelectors.getHomeTimezone));
  homeCoordinates$ = this.store$.pipe(select(HomesSelectors.getHomeCoordinates));
  homeAltitude$ = this.store$.pipe(select(HomesSelectors.getHomeAltitude));
  homeAddress$ = this.store$.pipe(select(HomesSelectors.getHomeAddress));
  homeName$ = this.store$.pipe(select(HomesSelectors.getHomeName));
  currentHomeCountry$ = this.store$.pipe(select(HomesSelectors.getCurrentHomeCountry));
  isFrance$ = this.store$.pipe(select(HomesSelectors.isFrance));
  electricitySchedule$ = this.store$.pipe(select(HomesSelectors.electricitySchedule));
  isSupportedCountry$ = this.store$.pipe(select(HomesSelectors.isSupportedCountry));
  currencySymbol$ = this.store$.pipe(select(HomesSelectors.currencySymbol));
  currentHomeHasNDL$ = this.store$.pipe(select(HomesSelectors.currentHomeHasNDL));

  hasDefaultElectricitySchedule$ = this.store$.pipe(select(HomesSelectors.hasDefaultElectricitySchedule));
  supportElectricitySchedule$ = this.store$.pipe(select(HomesSelectors.supportElectricitySchedule));
  electricityPowerThreshold$ = this.store$.pipe(select(HomesSelectors.electricityPowerThreshold));
  electricityPowerUnit$ = this.store$.pipe(select(HomesSelectors.electricityPowerUnit));
  defaultElectricityContractId$ = this.store$.pipe(select(HomesSelectors.defaultElectricityContractId));
  hasElectricitySchedules$ = this.store$.pipe(select(HomesSelectors.hasElectricitySchedules));
  productionElectricitySchedule$ = this.store$.pipe(select(HomesSelectors.productionElectricitySchedule));

  homesSuccess$ = this.actionOfType(HomesActions.getHomesSuccess.type);
  homesStatusSuccess$ = this.actionOfType(HomesActions.getHomeStatusSuccess.type);
  homeSelected$ = this.actionOfType(HomesActions.selectHome.type);
  currentStatusLoaded$ = this.store$.pipe(select(HomesSelectors.currentStatusLoaded));
  currentConfigLoaded$ = this.store$.pipe(select(HomesSelectors.currentConfigLoaded));
  hasBNSE$ = this.store$.pipe(select(HomesSelectors.hasBNSE));
  hasNLE$ = this.store$.pipe(select(HomesSelectors.hasNLE));
  hasNLG$ = this.store$.pipe(select(HomesSelectors.hasNLG));

  homeType$ = this.store$.pipe(select(HomesSelectors.getHomeType));

  heatingSchedules$ = this.store$.pipe(select(HomesSelectors.heatingSchedules));
  coolingSchedules$ = this.store$.pipe(select(HomesSelectors.coolingSchedules));
  autoSchedules$ = this.store$.pipe(select(HomesSelectors.autoSchedules));

  temperatureControlMode$ = this.store$.pipe(select(HomesSelectors.temperatureControlMode));

  thermMode$ = this.store$.pipe(select(HomesSelectors.thermMode));
  thermModeEndTime$ = this.store$.pipe(select(HomesSelectors.thermModeEndTime));

  coolingMode$ = this.store$.pipe(select(HomesSelectors.coolingMode));
  coolingModeEndTime$ = this.store$.pipe(select(HomesSelectors.coolingModeEndTime));

  autoTempMode$ = this.store$.pipe(select(HomesSelectors.autoTempMode));
  autoModeEndTime$ = this.store$.pipe(select(HomesSelectors.autoModeEndTime));

  currentModeEndTime$ = this.store$.pipe(select(HomesSelectors.currentModeEndTime));

  thermEndDate$ = this.store$.pipe(select(HomesSelectors.thermEndDate));
  supportsProduction$ = this.store$.pipe(select(HomesSelectors.supportsProduction));
  supportsOverproduction$ = this.store$.pipe(select(HomesSelectors.supportsOverproduction));

  hgTemperature$ = this.store$.pipe(select(HomesSelectors.hgTemperature));

  homeThermostats$ = this.store$.pipe(select(HomesSelectors.homeThermostats));
  supportsCooling$ = this.store$.pipe(select(HomesSelectors.supportsCooling));
  hasThermostat$ = this.store$.pipe(select(HomesSelectors.hasThermostat));

  supportedDevicesInSchedules$ = this.store$.pipe(select(HomesSelectors.supportedDevicesInSchedules));

  hasRoomControlledByUnsupportedDevice$ = this.store$.pipe(select(HomesSelectors.hasRoomControlledByUnsupportedDevice));
  energyType$ = this.store$.pipe(select(HomesSelectors.getEnergyType));
  homeSize$ = this.store$.pipe(select(HomesSelectors.getHomeSize));
  heatingSystem$ = this.store$.pipe(select(HomesSelectors.getHeatingSystem));
  homeHeatingController$ = this.store$.pipe(select(HomesSelectors.getHomeHeatingController));

  thermDefaultSetpointDuration$ = this.store$.pipe(select(HomesSelectors.thermSetpointDefaultDuration));

  loadHomes(params: any = {}): Observable<Action> {
    this.store$.dispatch(HomesActions.getHomes({ payload: params }));
    return this.actions$.pipe(ofType(HomesActions.getHomesSuccess), take(1));
  }

  loadHomeCoachs(params = {}): Observable<Action> {
    this.store$.dispatch(HomesActions.getHomeCoachs({ payload: params }));
    return this.actions$.pipe(ofType(HomesActions.getHomeCoachsSuccess, HomesActions.getHomeCoachsFailure), take(1));
  }

  /**
   * Loads data of the current home
   * @param id ID of current home from url
   * @param params Parameters of the request
   * @returns Observable on action of type GetHomesSuccess when it is fired
   */
  loadHome(id: string, params): Observable<Action> {
    this.store$.dispatch(HomesActions.getHomes({ payload: params }));

    return this.actions$.pipe(
      ofType(HomesActions.getHomesSuccess),
      tap(() => {
        this.selectHome(id);
      }),
      take(1),
    );
  }

  selectHome(id: string) {
    this.store$.dispatch(HomesActions.selectHome({ payload: id }));

    return this.actions$.pipe(ofType(HomesActions.selectHome), take(1));
  }

  loadHomeHeatingController(gatewayId: string) {
    this.store$.dispatch(HomesActions.getHomeHeatingController({ payload: { gatewayId } }));
    return this.actions$.pipe(
      ofType(HomesActions.getHomeHeatingControllerSuccess, HomesActions.getHomeHeatingControllerFailure),
      take(1),
    );
  }

  loadHeatingSystem(homeId: string) {
    this.store$.dispatch(HomesActions.getHeatingSystem({ payload: { homeId } }));
    return this.actions$.pipe(
      ofType(HomesActions.getHeatingSystemSuccess, HomesActions.getHeatingSystemFailure),
      take(1),
    );
  }

  /**
   * Loads status of current home modules
   * @param id ID of current home
   * @returns Observable on action of type GetHomesStatusSuccess when it is fired
   */
  loadHomeStatus(params: GetHomeInfoPayload): Observable<GetHomeStatusSuccess | GetHomeStatusFailure> {
    this.store$.dispatch(HomesActions.getHomeStatus({ payload: params }));

    return this.actions$.pipe(ofType(HomesActions.getHomeStatusSuccess), take(1));
  }

  getReadonlyDeviceState() {
    this.store$.dispatch(HomesActions.getReadonlyDeviceState());

    return this.actions$.pipe(ofType(HomesActions.getReadonlyDeviceStateSuccess), take(1));
  }

  /**
   * Loads config of current home modules
   * @param id ID of current home
   * @returns Observable on action of type GetHomesConfigSuccess when it is fired
   */
  loadHomeConfig(params: GetHomeInfoPayload): Observable<GetHomeConfigSuccess | GetHomeConfigFailure> {
    this.store$.dispatch(HomesActions.getHomeConfig({ payload: params }));

    return this.actions$.pipe(ofType(HomesActions.getHomeConfigSuccess, HomesActions.getHomeConfigFailure), take(1));
  }

  /**
   * Sets config of current home modules
   * @param homeId ID of home we want to set the modules of
   * @param config New config of modules
   * @returns Observable on action of type SetConfigSuccess when it is fired
   */
  setConfig(homeId: string, config): Observable<Action> {
    const params = {
      home_id: homeId,
      home: config,
    } as SetHomeConfigPayload;

    this.store$.dispatch(HomesActions.setHomeGatewayConfig({ payload: params }));
    return this.actions$.pipe(
      ofType(HomesActions.setHomeGatewayConfigSuccess, HomesActions.setHomeGatewayConfigFailure),
      take(1),
    );
  }

  /**
   * Sets config of current home modules
   * @param id ID of home we want to set the modules of
   * @param config New config of modules
   * @returns Observable on action of type SetConfigSuccess when it is fired
   */
  setState(params: { id: string; modules?; rooms? }, header?: Record<string, string>): Observable<HomesActionsUnion> {
    const setStatePayload = { home: params } as SetStatePayload;
    this.store$.dispatch(HomesActions.setHomeState({ payload: setStatePayload, header }));
    return this.actions$.pipe(
      ofType(HomesActions.setHomeStateSuccess, HomesActions.setHomeStateError, HomesActions.setHomeStateFailure),
      map((action) => {
        if ((action as any)?.payload?.error?.code === '7') {
          return action;
        }
        if (action.type === HomesActions.setHomeStateError.type) {
          throw action;
        } else {
          return action;
        }
      }),
      take(1),
    );
  }

  /**
   * Ack for local resources in case of error 31
   * @param params parameters about room or module to fix local resources
   */
  ackLocalResources(params: { home_id: string; device_id: string; modules?; rooms? }, header?: Record<string, string>) {
    const ackLocalResourcesPayload = params as AckLocalResourcesPayload;
    this.store$.dispatch(HomesActions.ackLocalResources({ payload: ackLocalResourcesPayload, header }));
    return this.actions$.pipe(
      ofType(HomesActions.ackLocalResourcesSuccess, HomesActions.ackLocalResourcesFailure),
      take(1),
    );
  }

  /**
   * Updates the location and timezone of a place
   * @param placeUpdate Parameters of the request:
   * If lat and lng, will update location and timezone; if only timezone, will update only timezone
   */
  updatePlace(placeUpdate: PlaceUpdate): void {
    this.store$.dispatch(HomesActions.updateHomePlace({ payload: placeUpdate }));
  }

  /**
   * Loads users of home
   * @param homeId ID of current home
   * @returns Observable on action of type GetHomeUsersSuccess when it is fired
   */
  loadHomeUsers(homeId: string): Observable<Action> {
    this.store$.dispatch(HomesActions.getHomeUsers({ payload: homeId }));

    return this.actions$.pipe(ofType(HomesActions.getHomeUsersSuccess), take(1));
  }

  /**
   * Remove a user from the home
   * @param homeId ID of the home
   * @param userId ID of the user
   * @returns Observable on action of type RemoveUserFromHomeSuccess when it is fired
   */
  removeUserFromHome(homeId: string, userId: string): Observable<Action> {
    const payload = { homeId, userId };

    this.store$.dispatch(HomesActions.removeUserFromHome({ payload }));

    return this.actions$.pipe(ofType(HomesActions.removeUserFromHomeSuccess), take(1));
  }

  /**
   * Generates a home admin access code
   * @param homeId ID of current home
   * @returns Observable on action of type GetAdminAccessCodeSuccess when it is fired
   */
  getAdminAccessCode(homeId: string): Observable<Action> {
    this.store$.dispatch(HomesActions.getAdminAccessCode({ payload: homeId }));

    return this.actions$.pipe(ofType(HomesActions.getAdminAccessCodeSuccess), take(1));
  }

  /**
   * Hides the error 400 modal
   */
  hideErrorModal(): void {
    this.store$.dispatch(HomesActions.hideErrorModal());
  }

  getHomeNFA(params: { home_id: string; app_type: string }) {
    this.store$.dispatch(HomesActions.getHomeNFA({ payload: params }));

    return this.actions$.pipe(
      ofType(HomesActions.getHomeNFASuccess, HomesActions.getHomeNFAFailure),
      take(1),
      map((action: GetHomeNFASuccess | GetHomeNFAFailure) => action.payload),
    );
  }

  /**
   * Set home type
   */
  setHomeType(params: { home: SetHomeTypePayload }) {
    this.store$.dispatch(HomesActions.setHomeType({ payload: params }));

    return this.actions$.pipe(ofType(HomesActions.setHomeTypeSuccess), take(1));
  }

  /**
   * Set home heating system (energy type, home type, home size)
   */
  setHomeHeatingSystem(params: SetHeatingTypeForHousePayload) {
    this.store$.dispatch(HomesActions.setHomeHeatingSystem({ payload: params }));

    return this.actions$.pipe(ofType(HomesActions.setHomeHeatingSystemSuccess), take(1));
  }

  /**
   * Set home heating system (energy type, home type, home size)
   */
  setHeatingControllerHeatingSystem(params: SetHeatingTypeForHeatingControllerPayload) {
    this.store$.dispatch(HomesActions.setHeatingControllerHeatingSystem({ payload: params }));
    // The effect dispatches getHeatingSystemSuccess/Failure (not setHeatingControllerHeatingSystemSuccess)
    return this.actions$.pipe(
      ofType(HomesActions.getHeatingSystemSuccess, HomesActions.getHeatingSystemFailure),
      take(1),
    );
  }

  setRHCConfiguration(payload: RHCConfigurationPayload): Observable<Action> {
    this.store$.dispatch(HomesActions.setRHCConfiguration({ payload }));
    return this.actions$.pipe(ofType(HomesActions.setRHCConfigurationSuccess), take(1));
  }

  getHomeHeatingControllers(gatewayId: string): Observable<Action> {
    this.store$.dispatch(HomesActions.getHomeHeatingController({ payload: { gatewayId } }));
    return this.actions$.pipe(ofType(HomesActions.getHomeHeatingControllerSuccess), take(1));
  }

  /**
   * Set home heating system (energy type, home type, home size)
   */
  setHomeSize(params: SetHomeSizePayload): Observable<SetHomeSizeSuccess> {
    this.store$.dispatch(HomesActions.setHomeSize({ payload: params }));
    return this.actions$.pipe(ofType(HomesActions.setHomeSizeSuccess), take(1));
  }

  loadForecast(payload: LoadForecastPayload): Observable<LoadForecastSuccess> {
    this.store$.dispatch(HomesActions.loadForecast({ payload }));

    return this.actions$.pipe(ofType(HomesActions.loadForecastSuccess), take(1));
  }

  private actionOfType<T = Action>(action: string) {
    const obs$ = this.actions$.pipe(ofType(action), publishReplay(1)) as ConnectableObservable<T>;
    obs$.subscribe();
    obs$.connect();
    return obs$;
  }

  removeDeviceFromHome(homeId: string, deviceId: string) {
    const params = { home_id: homeId, device_id: deviceId };
    this.store$.dispatch(HomesActions.removeDeviceFromHome({ payload: params }));
  }

  changeThermHomeMode(homeId: string, therm_mode: string, endtime?: number) {
    this.store$.dispatch(HomesActions.setThermMode({ homeId, therm_mode, endtime }));
    return this.actions$.pipe(ofType(HomesActions.setThermModeSuccess, HomesActions.setThermModeFailure), take(1));
  }

  setThermPoint(homeId: string, room_id: string, mode: SetThermPointMode, endtime?: number | null, temp?: number) {
    this.store$.dispatch(HomesActions.setThermPoint({ params: { home_id: homeId, room_id, mode, endtime, temp } }));
    return this.actions$.pipe(ofType(HomesActions.setThermPointSuccess, HomesActions.setThermPointFailure), take(1));
  }

  setHeatingMode(homeId: string, thermMode?: ThermModes, thermModeEndtime?: number) {
    this.store$.dispatch(
      HomesActions.setThermoregulationHeatingMode({
        payload: {
          home: {
            id: homeId,
            temperature_control_mode: TemperatureControlModes.HEATING,
            therm_mode: thermMode,
            therm_mode_endtime: thermModeEndtime,
          },
        },
      }),
    );

    return this.actions$.pipe(
      ofType(HomesActions.setThermoregulationHeatingModeSuccess, HomesActions.setThermoregulationHeatingModeFailure),
      take(1),
    );
  }

  setCoolingMode(homeId: string, coolingMode?: CoolingModes, coolingModeEndtime?: number) {
    this.store$.dispatch(
      HomesActions.setThermoregulationCoolingMode({
        payload: {
          home: {
            id: homeId,
            temperature_control_mode: TemperatureControlModes.COOLING,
            cooling_mode: coolingMode,
            cooling_mode_endtime: coolingModeEndtime,
          },
        },
      }),
    );

    return this.actions$.pipe(
      ofType(HomesActions.setThermoregulationCoolingModeSuccess, HomesActions.setThermoregulationCoolingModeFailure),
      take(1),
    );
  }

  setAutoMode(homeId: string, autoTempMode?: AutoTempModes, autoModeEndtime?: number) {
    this.store$.dispatch(
      HomesActions.setThermoregulationAutoMode({
        payload: {
          home: {
            id: homeId,
            temperature_control_mode: TemperatureControlModes.AUTO,
            auto_temp_mode: autoTempMode,
            auto_mode_endtime: autoModeEndtime,
          },
        },
      }),
    );

    return this.actions$.pipe(
      ofType(HomesActions.setThermoregulationAutoModeSuccess, HomesActions.setThermoregulationAutoModeFailure),
      take(1),
    );
  }

  loadReportsComparisonData(payload: LoadComparisonDataPayload): Observable<LoadComparisonDataSuccess> {
    this.store$.dispatch(HomesActions.loadReportsComparison({ payload }));

    return this.actions$.pipe(ofType(HomesActions.loadReportsComparisonSuccess), take(1));
  }

  removeHome(id: string): void {
    this.store$.dispatch(HomesActions.removeHome({ payload: { id } }));
  }
}
